// IP 合作「费用预算」展示表格（内包表单视角 / 谈判视角 / 外包版权方视角共用）。
// ProTablePlus 外壳 5 列对齐设计稿：预算总金额（rowSpan 跨明细行）/ 授权费类型（LF/MG/Royalty 勾选+名称）/
// 报价金额、分成占比 / 回复 / 意见反馈；
// 可编辑单元格用 IPCooperationDemandFormModel + model 既有字段 key 渲染（noStyle 直连外层 form）；
// 「回复/意见反馈」列从 detail.l2_info.negotiation_items 只读回显（对齐 CoreTermOfferTable 范式）；
// 场景模式：internal=内包（报价可编辑 + 回复只读）/ external=外包（报价只读 + 回复/意见可编辑，ActionCell/OpinionCell）；
// diff 态复用 RoundDiffCell / ActionDiffCell（新值着色 + 历史值灰删除线）。

import React, { useMemo } from 'react';
import { Form, Message, Tag } from '@bedrock/components';
import { ProTablePlus } from '@bedrock/eed-react';
import type { IProTablePlusColumn } from '@bedrock/eed-react/lib/components/ProTablePlus/interface';
import Decimal from 'decimal.js';
import {
  ActionCell,
  ACTION_TAG_COLOR,
  ACTION_TEXT_MAP,
  OpinionCell,
  createRenderBatchEditTitle,
  getOpinionTitle,
} from '../NegotiationTable';
import type {
  NegotiationEditState,
  NegotiationItem,
} from '../NegotiationTable';
import IPCooperationDemandFormModel from '../IPCooperationDemandFormModel';
import CurrencyType from '@mpc/common/src/components/SForm/SFormElement/CurrencyType';
import RoundDiffCell, { OLD_VALUE_CLASS } from '../RoundDiff/RoundDiffCell';
import ActionDiffCell from '../RoundDiff/ActionDiffCell';
import type { BudgetDiffMap, CoreTermItemDiffMap } from '../RoundDiff/types';

// 预算条款 item_key（negotiation_items 回复/意见回显 + diff 数据源；接口未 ready 前端 mock 约定，后端契约接入后对齐）
const BUDGET_ITEM_KEY = 'estimated_amount';

// 单元格 form item label 隐藏（Tailwind 任意值父选择器；对齐 app 层 index.less .ip-cooperation-demand-core-term-form-item 效果，
// 共享层不依赖 app 样式）
const FORM_CELL_CLASS = 'mb-0 [&_.rock-form-item-label]:hidden';

/** 费用预算固定明细行（授权金 LF / 保底金 MG / 分成 Royalty） */
interface BudgetRow {
  __row_key__: string;
  /** 行标识（lf/mg/royalty，diff key 前缀 + 报价列分派用） */
  item_key: 'lf' | 'mg' | 'royalty';
  /** 授权费类型列展示名 */
  item_name: string;
  /** 勾选字段 key（singlecheckbox，未勾选 → 报价列隐藏） */
  enabledKey: string;
  /** 报价金额字段 key（LF/MG 用） */
  amountKey?: string;
  /** 分成方式字段 key（Royalty 用） */
  royaltyTypeKey?: string;
  /** 分成占比字段 key（Royalty 用） */
  royaltyRateKey?: string;
}

/** 固定 3 行明细（对齐设计稿：授权金 LF / 保底金 MG / 分成 Royalty；字段 key 对齐接口 IpCooperationL2DesignForm） */
const BUDGET_ROWS: BudgetRow[] = [
  {
    __row_key__: 'lf',
    item_key: 'lf',
    item_name: '授权金（LF）',
    enabledKey: 'enable_license_fee',
    amountKey: '$budget_lf_amount',
  },
  {
    __row_key__: 'mg',
    item_key: 'mg',
    item_name: '保底金（MG）',
    enabledKey: 'enable_min_guarantee',
    amountKey: '$budget_mg_amount',
  },
  {
    __row_key__: 'royalty',
    item_key: 'royalty',
    item_name: '分成（Royalty）',
    enabledKey: 'enable_royalty',
    royaltyTypeKey: 'royalty_type',
    royaltyRateKey: 'royalty_rate',
  },
];

/** 单元格字段渲染配置（外层组装：DEFAULT_MODEL_CONF + globalItemProps.view + renderData；itemCols/renderType 由组件内部固定） */
type OfferModelConf = Omit<
  React.ComponentProps<typeof IPCooperationDemandFormModel>,
  'itemCols' | 'renderType'
>;

export interface BudgetOfferTableProps {
  /** 谈判条款列表（detail.l2_info.negotiation_items，回复/意见反馈回显数据源） */
  items?: NegotiationItem[];
  /** 单元格字段渲染配置（含 renderData/optionMap/编辑态 view），内部按 model 字段 key 逐格渲染（noStyle 直连 form） */
  modelConf?: OfferModelConf;
  /** 历史轮次对比 diff（key=total_amount/lf_amount/mg_amount/royalty_type/royalty_rate）；
   *  传了则对应单元格改渲染 DiffCell（最新值着色 + 历史值删除线），不传保持原 form 渲染 */
  diffMap?: BudgetDiffMap;
  /** 历史轮次对比 diff（key=BUDGET_ITEM_KEY）：版权方回复/意见反馈列对比用；传了则回复/意见列改渲染 DiffCell */
  itemDiffMap?: CoreTermItemDiffMap;
  /** 对比态开关：仅 compareEnabled=true 时展示 diff 样式（缺省 false）；SecondaryDetailContent 传 compareActive */
  compareEnabled?: boolean;
  /** 场景模式：external=外包（报价只读 + 回复/意见可编辑）；internal/缺省=内包（报价可编辑 + 回复/意见只读） */
  mode?: 'internal' | 'external';
  /** 是否可编辑（mode='external' 时回复/意见列可编辑；外包端由外层 editable 传入） */
  editable?: boolean;
  /** 编辑状态（mode='external'；key=BUDGET_ITEM_KEY） */
  editState?: Record<string, NegotiationEditState>;
  /** 单条编辑变更回调 */
  onEditChange?: (
    itemKey: string,
    patch: Partial<NegotiationEditState>
  ) => void;
  /** 批量操作（mode='external' 时回复列表头批量编辑 Popover，对齐 CoreTermTable） */
  onBatchAction?: (action: string, itemKeys?: string[]) => void;
  /** 是否展示回复/意见反馈两列（方案设计态传 false 隐藏；谈判视角/外包缺省 true） */
  showReplyColumns?: boolean;
}

/** 预算金额 diff 值渲染器：diff 值结构 {value,currency}，用 CurrencyType 展示（新值着色 / 旧值灰删除线） */
const renderBudgetMoneyDiff =
  (isOld = false) =>
  (v: unknown) => {
    const a = (v ?? {}) as any;
    return (
      <CurrencyType
        view
        value={{ value: a?.value, currency: a?.currency }}
        currencyColor={isOld ? 'grey' : undefined}
        currencyStyle={{ textDecoration: isOld ? 'line-through' : undefined }}
        textStyle={{ textDecoration: isOld ? 'line-through' : undefined }}
      />
    );
  };

const BudgetOfferTable: React.FC<BudgetOfferTableProps> = ({
  items = [],
  modelConf,
  diffMap,
  itemDiffMap,
  compareEnabled,
  mode = 'internal',
  editable = false,
  editState = {},
  onEditChange,
  onBatchAction,
  showReplyColumns = true,
}) => {
  const isView = Boolean(modelConf?.globalItemProps?.view);
  const form = Form.useFormInstance();
  // 费用预算整体校验（hidden Form.Item 承载）：① 授权费类型 LF/MG/Royalty 至少勾选一项；
  // ② 勾选的 LF/MG 金额之和须等于预算总金额（estimated_amount）。提交/暂存（form.validateFields）
  // 时触发一次，失败用 Message.error 显式提示（hidden 字段不展示错误）。只读态（isView）不渲染该校验项。
  const validateBudgetOffer = () => {
    const v = form.getFieldsValue();
    if (
      !v?.enable_license_fee &&
      !v?.enable_min_guarantee &&
      !v?.enable_royalty
    ) {
      Message.error('请至少勾选一种授权费类型');
      return Promise.reject(new Error('请至少勾选一种授权费类型'));
    }
    if (v?.enable_license_fee || v?.enable_min_guarantee) {
      const lf = v?.enable_license_fee ? Number(v?.license_fee_amount ?? 0) : 0;
      const mg = v?.enable_min_guarantee
        ? Number(v?.min_guarantee_amount ?? 0)
        : 0;
      const sum = new Decimal(lf).plus(mg);
      if (!sum.eq(Number(v?.estimated_amount ?? 0))) {
        Message.error('授权金与保底金金额之和需等于预算总金额');
        return Promise.reject(
          new Error('授权金与保底金金额之和需等于预算总金额')
        );
      }
    }
    return Promise.resolve();
  };
  // 对比态开关门控：仅 compareEnabled=true 时展示 diff 样式（缺省 false）；diff 数据仅在对比态被读取
  const activeDiffMap = compareEnabled ? diffMap : undefined;
  const activeItemDiffMap = compareEnabled ? itemDiffMap : undefined;
  // 外层 form 受控值（读勾选态 enabledKey：未勾选 → 报价列隐藏）
  const formData = modelConf?.renderData?.formData ?? {};
  // 外包模式：回复列表头批量编辑 Popover（对齐 CoreTermTable createRenderBatchEditTitle）
  const renderBatchEditTitle = createRenderBatchEditTitle({
    editable,
  });
  // 详情条款按 item_key 索引（回显 action/opinion）
  const itemsByKey = useMemo(() => {
    const map: Record<string, NegotiationItem> = {};
    (items || []).forEach((i) => {
      if (i.item_key) map[i.item_key] = i;
    });
    return map;
  }, [items]);
  // 预算条款行（外包可编辑回复/意见时：绑定 BUDGET_ITEM_KEY，对齐内包 diff 约定）
  const budgetItem = {
    ...(itemsByKey[BUDGET_ITEM_KEY] || {}),
    item_key: BUDGET_ITEM_KEY,
  };

  // 预算总金额列 rowSpan：第一行占满 3 明细行，其余行不渲染（对齐设计稿大单元格）；verticalAlign 垂直居中
  const budgetTotalOnCell = (_record: unknown, index: number) => {
    if (index === 0)
      return {
        rowSpan: BUDGET_ROWS.length,
        style: { verticalAlign: 'middle' },
      };
    return { rowSpan: 0 };
  };

  // 回复/意见列 rowSpan：整表一个预算条款（BUDGET_ITEM_KEY），跨 3 明细行合并（对齐预算总金额列合并模式）
  const replyOnCell = (_record: unknown, index: number) => {
    if (index === 0)
      return {
        rowSpan: BUDGET_ROWS.length,
        style: { verticalAlign: 'middle' },
      };
    return { rowSpan: 0 };
  };

  // 报价金额单元格表单字段渲染（noStyle 直连外层 form；label 经 FORM_CELL_CLASS Tailwind 隐藏）
  const renderFormCell = (formItemKey: string, col = 24) =>
    modelConf ? (
      <IPCooperationDemandFormModel
        {...modelConf}
        renderType="row"
        itemCols={[{ formItemKey, className: FORM_CELL_CLASS, col }]}
      />
    ) : (
      '-'
    );

  // 5 列：预算总金额 / 授权费类型 / 报价金额、分成占比 / 回复 / 意见反馈
  const columns = useMemo<IProTablePlusColumn<BudgetRow>[]>(
    () => [
      {
        title: '预估总金额',
        dataIndex: 'estimated_amount',
        // 内包只读态（isView）收窄前置列，使前面列合计=640、回复100/意见400（W=1140），
        // 缩放后回复/意见列与权益表格对齐；编辑态（isView=false）保持 300 宽列容纳可编辑控件。
        width: mode === 'external' ? 280 : isView ? 240 : 300,
        onCell: budgetTotalOnCell,
        // rowSpan 合并：仅第一行渲染字段，其余行返回 null（双保险，防 rowSpan=0 仍调用 render 导致字段重复渲染）
        render: (_v: unknown, _row: BudgetRow, index: number) => {
          if (index !== 0) return null;
          const diff = activeDiffMap?.total_amount
          if (diff) {
            return (
              <RoundDiffCell
                diff={diff}
                renderNewValue={renderBudgetMoneyDiff()}
                renderOldValue={renderBudgetMoneyDiff(true)}
              />
            );
          }
          return renderFormCell('$budget_total_amount');
        },
      },
      {
        title: '授权费类型',
        dataIndex: 'item_name',
        width: 160,
        render: (_v: string, row: BudgetRow) => {
          // 只读场景不渲染 checkbox，直接展示类型名称（对齐列标题语义）；编辑态仍渲染 singlecheckbox 启停勾选
          if (isView) return row.item_name;
          return renderFormCell(row.enabledKey);
        },
      },
      {
        title: '填写金额/分成比例',
        dataIndex: 'offer_value',
        // 外包内容列总宽统一 700（预算280+授权费类型160+报价260，对齐上线联动明细/授权信息/权益三表回复列 x）
        width: mode === 'external' ? 260 : isView ? 240 : 300,
        render: (_v: unknown, row: BudgetRow) => {
          // 勾选态控制：编辑态未勾选 → 隐藏（不渲染字段 → 天然跳过必填校验）；只读态（含 diff 态）始终按字段 view 渲染
          const enabled = formData?.[row.enabledKey];
          if (!enabled && !isView)
            return <span className="text-color-3">--</span>;
          if (row.amountKey) {
            // LF/MG：金额单元格（币种 + 单位 tag，CurrencyType showAmountOrder 自动出「万/百万」）
            const diff =
              activeDiffMap?.[
                row.item_key === 'lf' ? 'lf_amount' : 'mg_amount'
              ];
            if (diff) {
              return (
                <RoundDiffCell
                  diff={diff}
                  renderNewValue={renderBudgetMoneyDiff()}
                  renderOldValue={renderBudgetMoneyDiff(true)}
                />
              );
            }
            return renderFormCell(row.amountKey);
          }
          // Royalty：分成方式 Select + 分成占比%（未勾选隐藏；diff 态分别渲染新旧）
          const typeDiff = activeDiffMap?.royalty_type;
          const rateDiff = activeDiffMap?.royalty_rate;
          if (typeDiff || rateDiff) {
            return (
              <div className="flex items-center gap-2">
                <RoundDiffCell
                  diff={typeDiff}
                  renderNewValue={(v) => <span>{String(v ?? '') || '--'}</span>}
                  renderOldValue={(v) => (
                    <span className={OLD_VALUE_CLASS}>
                      {String(v ?? '') || '--'}
                    </span>
                  )}
                />
                <RoundDiffCell
                  diff={rateDiff}
                  renderNewValue={(v) => (
                    <span>
                      {String((typeof v === 'number' ? v * 100 : v) ?? '')}%
                    </span>
                  )}
                  renderOldValue={(v) => (
                    <span className={OLD_VALUE_CLASS}>
                      {String((typeof v === 'number' ? v * 100 : v) ?? '')}%
                    </span>
                  )}
                />
              </div>
            );
          }
          return (
            <div className="flex items-center gap-2">
              {row.royaltyTypeKey ? (
                <div className="flex-1 min-w-0">
                  {renderFormCell(row.royaltyTypeKey)}
                </div>
              ) : null}
              {row.royaltyRateKey ? (
                <div className="flex-1 min-w-0">
                  {renderFormCell(row.royaltyRateKey)}
                </div>
              ) : null}
            </div>
          );
        },
      },
      {
        title:
          mode === 'external'
            ? renderBatchEditTitle([BUDGET_ITEM_KEY])
            : '回复',
        dataIndex: 'action',
        // 外包编辑态回复列放 3 个 Medium Tag（对齐 CoreTermTable editable 宽）；外包只读单 Tag（100，对齐上线联动明细）；内包只读 130
        // 内包只读态对齐权益表格回复列宽（100）
        width: mode === 'external' ? (editable ? 180 : 100) : isView ? 100 : 130,
        onCell: replyOnCell,
        // rowSpan 跨 3 明细行合并：仅第一行渲染回复，其余行返回 null（整表一个预算条款）
        render: (_v: unknown, _row: BudgetRow, index: number) => {
          if (index !== 0) return null;
          const actionDiff = activeItemDiffMap?.[BUDGET_ITEM_KEY]?.action;
          if (mode === 'external') {
            // 外包：回复列可编辑（ActionCell 稳定组件，绑定 BUDGET_ITEM_KEY；readOnly=!editable）
            return (
              <ActionCell
                row={budgetItem}
                itemsByKey={itemsByKey}
                editState={editState}
                editable={editable}
                readOnly={!editable}
                onEditChange={onEditChange}
                actionDiff={actionDiff}
              />
            );
          }
          const action = itemsByKey[BUDGET_ITEM_KEY]?.action || '';
          if (actionDiff) {
            // 历史轮次对比态：复用 ActionDiffCell（新值 Tag 按 diff 类型着色 + 旧值 Tag light-grey）
            return <ActionDiffCell diff={actionDiff} newAction={action} />;
          }
          if (!action) {
            return '--';
          }
          return (
            <Tag
              text={ACTION_TEXT_MAP[action] || action || '-'}
              color={ACTION_TAG_COLOR[action] || 'light-grey'}
            />
          );
        },
      },
      {
        title: getOpinionTitle(),
        dataIndex: 'opinion',
        // 外包意见反馈/说明列不设固定宽（弹性撑满，最小宽 100 防坍缩，对齐上线联动明细）；内包保持固定 400
        ...(mode === 'external' ? { minWidth: 100 } : { width: 400 }),
        onCell: replyOnCell,
        // rowSpan 跨 3 明细行合并：仅第一行渲染意见，其余行返回 null（整表一个预算条款）
        render: (_v: unknown, _row: BudgetRow, index: number) => {
          if (index !== 0) return null;
          const opinionDiff = activeItemDiffMap?.[BUDGET_ITEM_KEY]?.opinion;
          if (mode === 'external') {
            // 外包：意见反馈可编辑（OpinionCell 稳定组件，绑定 BUDGET_ITEM_KEY）
            return (
              <OpinionCell
                row={budgetItem}
                itemsByKey={itemsByKey}
                editState={editState}
                editable={editable}
                readOnly={!editable}
                onEditChange={onEditChange}
                opinionDiff={opinionDiff}
              />
            );
          }
          if (opinionDiff) {
            // 历史轮次对比态：最新值 + 历史值（灰删除线），保留换行
            return (
              <RoundDiffCell
                diff={opinionDiff}
                renderNewValue={(v) => (
                  <span className="whitespace-pre-line">
                    {String(v ?? '') || '--'}
                  </span>
                )}
                renderOldValue={(v) => (
                  <span className={`${OLD_VALUE_CLASS} whitespace-pre-line`}>
                    {String(v ?? '') || '--'}
                  </span>
                )}
              />
            );
          }
          return (
            <span className="whitespace-pre-line">
              {itemsByKey[BUDGET_ITEM_KEY]?.opinion || '--'}
            </span>
          );
        },
      },
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      itemsByKey,
      modelConf,
      isView,
      diffMap,
      itemDiffMap,
      compareEnabled,
      formData,
      mode,
      editable,
      editState,
      onEditChange,
      onBatchAction,
      renderBatchEditTitle,
      budgetItem,
    ]
  );

  // 方案设计态（showReplyColumns=false）：过滤回复/意见两列（后 2 列，保留预算总金额/授权费类型/报价金额分成占比 3 列）
  const visibleColumns = showReplyColumns ? columns : columns.slice(0, 3);

  return (
    <>
      <ProTablePlus<BudgetRow>
        key={isView ? 'view' : 'edit'}
        rowKey="__row_key__"
        verticalBorder
        columns={visibleColumns}
        data={BUDGET_ROWS}
        pagination={false}
        scroll={{ x: mode === 'external' ? '100%' : 'max-content' }}
      />
      {/* 费用预算整体校验承载（hidden）：提交/暂存时 form.validateFields 校验虚拟字段 budget_offer_validate，
          失败经 Message.error 提示；只读态（isView）不渲染，避免详情/谈判视角误校验 */}
      {!isView && (
        <Form.Item
          name="budget_offer_validate"
          labelStyle={{ display: 'none3' }}
          layout={'vertical'}
          rules={[{ validator: validateBudgetOffer }]}
        >
          <span style={{ display: 'none' }} />
        </Form.Item>
      )}
    </>
  );
};

export default BudgetOfferTable;
